//src/pages/AddPatientModal/editPatientModal.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { LARGE_FORM_MODAL_SX } from "../../utils/formModalStyles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { updatePatient, getAllPatients, registerPatient, syncPatientTypeForWorkflow } from "../../services/patientService";
import { create } from "../../services/PatientAdmissionService";
import { updateAdmission } from "../../services/EmergencyAdmissionService";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name should be of minimum 2 characters length"),
  gender: Yup.string().required("Gender is required"),
  dob: Yup.date()
      .required("DOB is required")
      .max(dayjs(), "DOB cannot be in the future"),
  address: Yup.string().required("Address is required"),
  aadhar: Yup.string()
    .notRequired() // Make Aadhar optional
    .test('aadhar-format', 'Aadhar should be a 12-digit number', function(value) {
      if (!value || value.length === 0) return true; // Allow empty values
      return /^\d{12}$/.test(value);
    }),
  email: Yup.string()
    .email("Enter a valid email")
    .notRequired(), // Make email optional
  mobile: Yup.string()
    .notRequired() // Make contact number optional
    .test('mobile-format', 'Contact Number should be a 10-digit number', function(value) {
      if (!value || value.length === 0) return true; // Allow empty values
      return /^\d{10}$/.test(value);
    }),
});

const EditPatientModal = ({ open, onClose, EmergencyId, admissionPatientName, admissionPatientContact, patient_info = {}, refreshList }) => {

  console.log('patient_info', patient_info); 
  const navigate = useNavigate();
  const location = useLocation();
  const { hasCapability, isPremiumPackage } = useSubscriptionCapabilities();
  const hasIpdCapability = hasCapability("IPD") || hasCapability("In Patient");
  // Emergency admission → IN only on Premium; Basic/Intermediate remain OUT
  const canCreateInPatient = isPremiumPackage && hasIpdCapability;
  const [current_step, setCurrentStep] = useState({
    new_patient: true,
    create_invoice: false,
    success: false,
  });

  const fetchExistingPatients = async () => {
    try {
      const patients = await getAllPatients(); 
      return patients;
    } catch (error) {
      console.error("Error fetching existing patients:", error);
      return []; 
    }
  };

  const checkForFieldDuplicates = async (values) => {
    const existingPatients = await fetchExistingPatients();
    const errors = {};
    existingPatients.forEach((patient) => {
      if (patient.id !== patient_info?.id) {
        if (String(patient.aadhar).trim() === String(values.aadhar).trim()) {
          errors.aadhar = "Aadhar already exists.";
        }
        if (String(patient.mobile).trim() === String(values.mobile).trim()) {
          errors.mobile = "Mobile number already exists.";
        }
        if (
          patient.email &&
          values.email &&
          patient.email.toLowerCase().trim() === values.email.toLowerCase().trim()
        ) {
          errors.email = "Email already exists.";
        }
      }
    });
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      name: patient_info?.name || admissionPatientName || "",
      address: patient_info?.address || "",
      aadhar: patient_info?.aadhar || "",
      email: patient_info?.email || "",
      dob: patient_info?.dob ? dayjs(patient_info.dob) : null,
      mobile: patient_info?.mobile || admissionPatientContact || "",
      gender: patient_info?.gender || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const duplicateErrors = await checkForFieldDuplicates(values);
      if (Object.keys(duplicateErrors).length > 0) {
        Object.entries(duplicateErrors).forEach(([field, message]) => {
          formik.setFieldError(field, message);
        });
        return;
      }
      const patient_req_body = {
        name: values.name,
        mobile: values.mobile,
        email: values.email,
        aadhar: values.aadhar,
        address: values.address,
        dob: dayjs(values.dob).format("YYYY-MM-DD"),
        gender: values.gender,
        patientType: canCreateInPatient ? "IN" : "OUT",
      };

      try {
        if (patient_info?.id) {
          patient_req_body.id = patient_info.id;

          const hasChanged =
            patient_info.name !== patient_req_body.name ||
            patient_info.mobile !== patient_req_body.mobile ||
            patient_info.email !== patient_req_body.email ||
            patient_info.aadhar !== patient_req_body.aadhar ||
            patient_info.address !== patient_req_body.address ||
            patient_info.gender !== patient_req_body.gender ||
            dayjs(patient_info.dob).format("YYYY-MM-DD") !== patient_req_body.dob ||
            patient_info.patientType !== patient_req_body.patientType;
          if (hasChanged) {
            await updatePatient(patient_info.id, patient_req_body);
            toast.success("Patient updated successfully!");
            if (typeof refreshList === 'function') await refreshList();
            onClose();
          } else {
            toast.info("No changes detected.");
          }
        } else {
          const patient_req_body = {
            name: admissionPatientName,
            mobile: admissionPatientContact,
            email: values.email,
            aadhar: values.aadhar,
            address: values.address,
            dob: dayjs(values.dob).format("YYYY-MM-DD"),
            gender: values.gender,
            patientType: canCreateInPatient ? "IN" : "OUT",
          };
          // registerPatient uses apiClient which automatically adds organizationId
          // This ensures patient is created in organization-specific table (e.g., 23_patient)
          const createdPatient = await registerPatient(patient_req_body);
          if (createdPatient?.portalCredentials) {
            toast.success(
              `Patient portal created. Temporary password: ${createdPatient.portalCredentials.password}`,
              { autoClose: 6000 }
            );
          }
          const admissionData = {
            patientId:createdPatient.id,
            admissionDate: new Date().toISOString(),
            admissionTime: new Date().toTimeString().split(" ")[0],
            status: "admit",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            admissionAmount: "500",
            diseaseDiagnose: "emergency",
            admissionType: "emergency",
          };
          // create uses apiClient which automatically adds organizationId as query parameter
          // This ensures admission is created in organization-specific table (e.g., 23_ipd_admissions)
          const newAdmission = await create(admissionData);
          if (createdPatient?.id) {
            // Premium emergency admission → IN; Basic/Intermediate stay OUT
            await syncPatientTypeForWorkflow(
              createdPatient.id,
              canCreateInPatient ? "IN" : "OUT",
              { force: !canCreateInPatient }
            );
            // updateAdmission uses apiClient which automatically adds organizationId as query parameter
            // This ensures emergency admission is updated in organization-specific table (e.g., 23_emergency_admission)
            await updateAdmission(EmergencyId, {
              patientId: createdPatient.id,
            });
            toast.success("Patient created and linked successfully!");
            if (typeof refreshList === 'function') await refreshList();
            onClose();
          } else {
            toast.error("Patient creation succeeded but no ID was returned.");
          }
        }
      } catch (error) {
        toast.error("Something went wrong!");
        console.error(error);
      }
    },
  });
  
  const handelCloseModalState = async () => {
    if (location.pathname === "/manage-patient") navigate("/manage-patient");
    if (location.pathname === "/path-patient") navigate("/path-patient");
    if (location.pathname === "/front-desk") navigate("/front-desk");

    await onClose();
    formik.resetForm();
    setCurrentStep({
      new_patient: true,
      create_invoice: false,
      success: false,
    });
  };

  const modalTitle = !!patient_info?.["id"] ? "View Linking Patient" : "Create Linking Patient";

  return (
    <BasicModal
      open={open}
      onClose={ handelCloseModalState }
      sx={LARGE_FORM_MODAL_SX}
      title={modalTitle}
    >
      <Box display="flex" flexDirection="column">
        {current_step.new_patient && (
          <>
            <Box>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="name"
                      name="name"
                      label="Name"
                      value={formik.values.name}
                      onChange={(e) => {
                        let inputValue = e.target.value;
                        
                        // Capitalize first letter
                        if (inputValue.length > 0) {
                          inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                        }
                        
                        formik.setFieldValue("name", inputValue);
                      }}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      id="gender"
                      name="gender"
                      fullWidth
                      select
                      label="Gender"
                      value={formik.values.gender}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.gender && Boolean(formik.errors.gender)
                      }
                    >
                      {["Male", "Female"].map((gender) => (
                        <MenuItem key={gender} value={gender}>
                          {gender}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="DOB"
                        format="YYYY-MM-DD"
                        value={formik.values.dob}
                        onChange={(value) =>
                          formik.setFieldValue("dob", value)
                        }
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error:
                              formik.touched.dob && Boolean(formik.errors.dob),
                            helperText:
                              formik.touched.dob && formik.errors.dob,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="aadhar"
                      name="aadhar"
                      label="Aadhar"
                      placeholder="12 digit Aadhar number"
                      value={formik.values.aadhar}
                      onChange={(e) => {
                        // Only allow digits and limit to 12 digits
                        const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                        formik.setFieldValue("aadhar", value);
                      }}
                      inputProps={{ maxLength: 12 }}
                      error={
                        formik.touched.aadhar &&
                        Boolean(formik.errors.aadhar)
                      }
                      helperText={
                        formik.touched.aadhar && formik.errors.aadhar
                          ? formik.errors.aadhar
                          : "Enter 12 digit Aadhar number"
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      label="Email"
                      placeholder="example@email.com"
                      value={formik.values.email}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow email characters including @ symbol (letters, numbers, dots, hyphens, underscores, @, and +)
                        if (value === "" || /^[a-zA-Z0-9._+-@]*$/.test(value)) {
                          formik.setFieldValue("email", value);
                        }
                      }}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={
                        formik.touched.email && formik.errors.email
                          ? formik.errors.email
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="mobile"
                      name="mobile"
                      label="Contact Number"
                      placeholder="10 digit phone number"
                      value={formik.values.mobile}
                      onChange={(e) => {
                        // Only allow digits and limit to 10 digits
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        formik.setFieldValue("mobile", value);
                      }}
                      inputProps={{ maxLength: 10 }}
                      error={
                        formik.touched.mobile && Boolean(formik.errors.mobile)
                      }
                      helperText={
                        formik.touched.mobile && formik.errors.mobile
                          ? formik.errors.mobile
                          : "Enter 10 digit phone number"
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="address"
                      name="address"
                      label="Address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.address &&
                        Boolean(formik.errors.address)
                      }
                      helperText={
                        formik.touched.address && formik.errors.address
                      }
                    />
                  </Grid>
                  {/* <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="patientType"
                      name="patientType"
                      label="Patient Type"
                      value={formik.values.patientType}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid> */}

                  {/* <Grid item xs={6}>
                      <TextField
                      fullWidth
                      id="patientType"
                      name="patientType"
                      label="Patient Type"
                      value={formik.values.patientType}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={12}>
                    <FormModalActions
                      onCancel={handelCloseModalState}
                      submitType="submit"
                      loading={formik.isSubmitting}
                    />
                  </Grid>
                </Grid>
              </form>
            </Box>
          </>
        )}
      </Box>
    </BasicModal>
  );
};

export default EditPatientModal;